<script setup lang="ts">
import { fetchMaterialIconOptions } from "@/service/IconServices";
import { useAppStore } from "@/store/app";
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  ChevronRightIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UICollapse, UICollapseItem, UIInputNumber, UISwitch } from "@gohighlevel/ghl-ui";
import { onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { CONSTANTS } from "../../util/constants";
import { defaultBtnPadding, defaultShadow } from "./../../util/default";
import ButtonThemes from "./ButtonThemes.vue";
import BorderStyle from "./Elements/BorderStyle.vue";
import ColorInput from "./Elements/ColorInput.vue";
import FontFamilyInput from "./Elements/FontFamilyInput.vue";
import IconPicker from "./Elements/IconPicker.vue";
import PaddingInput from "./Elements/PaddingInput.vue";
import PixelNumber from "./Elements/PixelNumber.vue";
import Shadow from "./Elements/Shadow.vue";
import RichTextEditor from "@/components/text-editor/index.vue";
const { t } = useI18n();
const store = useAppStore();

const props = defineProps({
  property: {
    type: Object,
    default() {
      return {
        label: "Submit",
        tag: "button",
        type: "submit",
        placeholder: "Submit",
        submitSubText: "",
        bgColor: "18BD5BFF",
        padding: defaultBtnPadding,
        border: 0,
        borderType: "none",
        borderColor: "FFFFFF",
        borderRadius: 6,
        shadow: defaultShadow,
        fullwidth: true,
        color: "FFFFFF",
        fontSize: 14,
        weight: 500,
        radius: 0,
        fontFamily: "Roboto",
        subTextColor: "000000",
        subTextFontSize: 14,
        subTextWeight: 200,
        subTextFontFamily: "Roboto",
        align: "center",
        fieldWidthPercentage: 100,
        enableIcon: false,
        iconType: "arrow_forward",
        iconPlacement: "right",
      };
    },
  },
});
const output = reactive({ ...props.property });
const showSubtext = ref(!!props.property.submitSubText);
const showThemeSelector = ref(false);
const showIcon = ref(!!props.property.enableIcon);

// Material Symbols: dynamically loaded list of icons (no local fallback)
const materialIconOptions = ref<Array<{ label: string; value: string }>>([]);

onMounted(async () => {
  materialIconOptions.value = await fetchMaterialIconOptions();
});

watch(
  () => props.property,
  (newVal) => {
    const keys = Object.keys(newVal);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      output[key] = newVal[key];
    }
  },
  {
    deep: true,
  }
);

watch(showSubtext, (newValue) => {
  if (!newValue) {
    // Clear subtext when toggle is turned off
    output.submitSubText = "";
    store.anyUnsavedChanges = true;
    emits("update:button", output);
  }
});

watch(showIcon, (newValue) => {
  output.enableIcon = newValue;
  if (!newValue) {
    // Reset icon settings when disabled
    output.iconType = "arrow_forward";
    output.iconPlacement = "right";
  } else {
    // Ensure defaults on first enable
    if (!output.iconType) {
      output.iconType = "arrow_forward";
    }
    if (!output.iconPlacement) {
      output.iconPlacement = "right";
    }
  }
  store.anyUnsavedChanges = true;
  emits("update:button", output);
});

const emits = defineEmits(["update:button"]);

const handlePaddingUpdate = (side: string, value: number) => {
  output.padding[side] = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
  emits("update:button", output);
};

const handleTextFieldStyling = (value, type) => {
  switch (type) {
    case "align":
      output.align = value;
      break;
    case "background":
      output.bgColor = value;
      // Set mapPrimaryColorToButtonColor to false when button background color is manually changed
      store.fieldStyle.mapPrimaryColorToButtonColor = false;
      break;
    case "border":
      output.border = parseFloat(value);
      break;
    case "borderColor":
      output.borderColor = value;
      break;
    case "borderType":
      output.borderType = value;
      break;
    case "cornerRadius":
      output.radius = parseFloat(value);
      break;
    case "fullWidth":
      output.fullwidth = value;
      if (
        CONSTANTS.SINGLE_LINE ===
          (store.deviceMode === "mobile"
            ? store.builderConfig.mobileLayout || store.builderConfig.layout
            : store.builderConfig.layout) &&
        value
      ) {
        output.fieldWidthPercentage = 100;
      }
      break;
    case "paddingTop":
      output.padding.top = value;
      break;
    case "paddingBottom":
      output.padding.bottom = value;
      break;
    case "paddingLeft":
      output.padding.left = value;
      break;
    case "paddingRight":
      output.padding.right = value;
      break;
    case "shadowColor":
      output.shadow.color = value;
      break;
    case "shadowHorizontal":
      output.shadow.horizontal = value;
      break;
    case "shadowVertical":
      output.shadow.vertical = value;
      break;
    case "shadowBlur":
      output.shadow.blur = value;
      break;
    case "shadowSpread":
      output.shadow.spread = value;
      break;
    case "textLabel":
      output.label = value;
      break;
    case "labelFontFamily":
      output.fontFamily = value;
      store.fontCollection.push(value);
      break;
    case "labelFontColor":
      output.color = value;
      break;
    case "subLabelFontColor":
      output.subTextColor = value;
      break;
    case "labelFontSize":
      output.fontSize = value;
      break;
    case "labelFontWeight":
      output.weight = value;
      break;
    case "subTextLabel":
      output.submitSubText = value;
      break;
    case "subLabelFontFamily":
      output.subTextFontFamily = value;
      store.fontCollection.push(value);
      break;

    case "subLabelFontSize":
      output.subTextFontSize = value;
      break;
    case "subLabelFontWeight":
      output.subTextWeight = value;
      break;
    case "fieldWidthPercentage":
      output.fieldWidthPercentage = value;
      break;
    case "enableIcon":
      output.enableIcon = value;
      break;
    case "iconType":
      output.iconType = value;
      break;
    case "iconPlacement":
      output.iconPlacement = value;
      break;
    default:
      break;
  }
  store.anyUnsavedChanges = true;
  store.addToHistory();
  emits("update:button", output);
};

const handleThemeSelection = (themeConfig: any) => {
  // Apply all theme properties to the button
  Object.keys(themeConfig).forEach((key) => {
    if (output.hasOwnProperty(key)) {
      output[key] = themeConfig[key];
    }
  });

  // Also apply the radius from borderRadius
  if (themeConfig.borderRadius !== undefined) {
    output.radius = themeConfig.borderRadius;
  }

  // Apply shadow properties if they exist
  if (themeConfig.shadow) {
    output.shadow = themeConfig.shadow;
  }

  store.anyUnsavedChanges = true;
  store.addToHistory();
  emits("update:button", output);

  // Don't hide theme selector after selection - user can continue trying themes
  // showThemeSelector.value = false;
};

defineExpose({
  showThemeSelector,
});
</script>

<template>
  <section id="button-sidebar-container">
    <!-- Show theme selector when active -->
    <ButtonThemes
      v-if="showThemeSelector"
      :current-button-config="{
        label: output.label,
        submitSubText: output.submitSubText,
      }"
      @back="showThemeSelector = false"
      @select-theme="handleThemeSelection"
    />

    <!-- Regular sidebar content -->
    <div v-else>
      <h6 class="field-name-sidebar">{{ t("common.button") }}</h6>

      <!-- Theme select preview card -->
      <div
        class="theme-select-card m-3 flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-2.5 transition-all hover:border-gray-300 hover:bg-gray-50"
        @click="showThemeSelector = true"
      >
        <!-- Preview buttons -->
        <div class="preview-box flex flex-col gap-2 rounded-lg border border-gray-200 p-2">
          <!-- Primary style preview -->
          <div
            class="w-[100px] cursor-default rounded-lg bg-blue-600 px-4 py-2 text-center text-sm font-medium leading-5 text-white"
          >
            {{ t("common.button") }}
          </div>

          <!-- Dark style preview -->
          <div
            class="w-[100px] cursor-default rounded-lg bg-gray-900 px-4 py-2 text-center text-sm font-medium leading-5 text-white"
          >
            {{ t("common.button") }}
          </div>
        </div>

        <!-- Select themes text & arrow -->
        <div class="flex w-full items-center justify-between pl-4 pr-1">
          <span class="text-base font-medium text-gray-700">
            {{ t("common.selectThemes") }}
          </span>
          <ChevronRightIcon class="h-5 w-5 text-gray-700" />
        </div>
      </div>

      <!-- Revamped collapsible sidebar structure  -->
      <UICollapse :accordion="true" :arrow-placement="'left'" :default-expanded-names="['content']">
        <!-- Content Section -->
        <UICollapseItem id="content" name="content">
          <template #header>
            <div>{{ t("common.content") }}</div>
          </template>
          <div class="collapse-content-container">
            <div class="collapse-content flex flex-col gap-0">
              <!-- Text Section -->
              <div class="flex flex-col gap-4">
                <div>
                  <div class="hl-text-xs-medium mb-2">{{ t("common.text") }}</div>
                  <div class="w-full">
                    <RichTextEditor
                      :model-value="output.label"
                      class="w-[324px]"
                      size="mid"
                      @update:model-value="(value) => handleTextFieldStyling(value, 'textLabel')"
                    />
                  </div>
                </div>

                <!-- Text Properties -->
                <div class="flex flex-col gap-4">
                  <FontFamilyInput
                    class="w-full"
                    :label="t('common.fontFamily')"
                    :value="output.fontFamily"
                    @update:value="(value) => handleTextFieldStyling(value, 'labelFontFamily')"
                  />
                  <div class="flex items-start gap-3">
                    <PixelNumber
                      :label="t('common.style.fontSize')"
                      :value="parseFloat(output.fontSize)"
                      @update:value="(value) => handleTextFieldStyling(value, 'labelFontSize')"
                    />
                    <PixelNumber
                      :label="t('common.style.fontWeight')"
                      :value="parseFloat(output.weight)"
                      :max="1800"
                      @update:value="(value) => handleTextFieldStyling(value, 'labelFontWeight')"
                    />
                  </div>
                </div>
              </div>

              <!-- Icon Section -->
              <div class="icons-container mt-4 flex flex-col gap-4">
                <!-- Enable Icon Toggle -->
                <div class="flex w-full items-center justify-between pb-2">
                  <div class="hl-text-xs-medium">{{ t("common.enableIcon") }}</div>
                  <UISwitch v-model:value="showIcon" />
                </div>

                <!-- Icon Configuration (shown when toggle is on) -->
                <div v-show="showIcon" class="flex w-full flex-col gap-4">
                  <!-- Material Symbols Icon Picker -->
                  <IconPicker
                    :label="t('common.iconType')"
                    :model-value="output.iconType"
                    @update:modelValue="(value: string) => handleTextFieldStyling(value, 'iconType')"
                  />

                  <!-- Icon Placement Radio Buttons -->
                  <div>
                    <div class="hl-text-xs-medium mb-2">{{ t("common.iconPlacement") }}</div>
                    <div class="icon-placement-grid grid grid-cols-2 gap-2">
                      <UIButton
                        id="icon-placement-left"
                        :type="output.iconPlacement === 'left' ? 'tertiary' : 'default'"
                        size="large"
                        class="placement-button w-full flex-1"
                        :class="{ 'placement-button-active': output.iconPlacement === 'left' }"
                        @click="handleTextFieldStyling('left', 'iconPlacement')"
                      >
                        {{ t("common.left") }}
                      </UIButton>
                      <UIButton
                        id="icon-placement-right"
                        :type="output.iconPlacement === 'right' ? 'tertiary' : 'default'"
                        size="large"
                        class="placement-button w-full flex-1"
                        :class="{ 'placement-button-active': output.iconPlacement === 'right' }"
                        @click="handleTextFieldStyling('right', 'iconPlacement')"
                      >
                        {{ t("common.right") }}
                      </UIButton>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Sub-Text Section -->
              <div class="subtext-container mt-4 flex flex-col gap-4">
                <!-- Subtext Toggle -->
                <div class="flex w-full items-center justify-between pb-2">
                  <div class="hl-text-xs-medium">{{ t("common.subText") }}</div>
                  <UISwitch v-model:value="showSubtext" />
                </div>

                <!-- Subtext Content (shown when toggle is on) -->
                <div v-show="showSubtext" class="flex w-full flex-col gap-4">
                  <div>
                    <div class="w-full">
                      <RichTextEditor
                        :model-value="output.submitSubText"
                        class="w-full"
                        size="mid"
                        :is-submit-message="true"
                        @update:model-value="
                          (value) => handleTextFieldStyling(value, 'subTextLabel')
                        "
                      />
                    </div>
                  </div>

                  <!-- Sub-Text Properties -->
                  <FontFamilyInput
                    class="w-full"
                    :label="t('common.fontFamily')"
                    :value="output.subTextFontFamily"
                    @update:value="(value) => handleTextFieldStyling(value, 'subLabelFontFamily')"
                  />
                  <div class="flex items-start gap-3">
                    <PixelNumber
                      :label="t('common.style.fontSize')"
                      :value="parseFloat(output.subTextFontSize)"
                      @update:value="(value) => handleTextFieldStyling(value, 'subLabelFontSize')"
                    />
                    <PixelNumber
                      :label="t('common.style.fontWeight')"
                      :value="parseFloat(output.subTextWeight)"
                      :max="1800"
                      @update:value="(value) => handleTextFieldStyling(value, 'subLabelFontWeight')"
                    />
                  </div>
                </div>
              </div>

              <!-- Full Width Toggle -->
              <div class="mt-4 flex w-full items-center justify-between pb-2">
                <div class="hl-text-xs-medium">{{ t("common.fullWidth") }}</div>
                <UISwitch
                  v-model:value="output.fullwidth"
                  @update:value="(value) => handleTextFieldStyling(value, 'fullWidth')"
                />
              </div>

              <!-- Field Width (only when not full width) -->
              <div v-show="!output.fullwidth" class="mt-3">
                <div class="hl-text-xs-medium mb-2">
                  {{ t("common.style.fieldWidth") }}
                </div>
                <UIInputNumber
                  id="fieldWidthInput"
                  size="large"
                  class="w-[20rem]"
                  :min="1"
                  :max="100"
                  :default-value="100"
                  :show-button="false"
                  :model-value="output.fieldWidthPercentage"
                  @update:model-value="
                    (value) => handleTextFieldStyling(value, 'fieldWidthPercentage')
                  "
                  @blur="store.addToHistory()"
                >
                  <template #suffix>
                    <div class="percent-text flex h-4 w-4 items-center justify-center">%</div>
                  </template>
                </UIInputNumber>
              </div>
            </div>
          </div>
        </UICollapseItem>

        <!-- Alignment Section (moved below content) -->
        <UICollapseItem id="alignment" name="alignment">
          <template #header>
            <div>{{ t("common.alignment") }}</div>
          </template>
          <div class="collapse-content-container">
            <div class="collapse-content">
              <div class="alignment-grid grid grid-cols-3 gap-2">
                <UIButton
                  id="align-left"
                  :type="output.align === 'left' ? 'tertiary' : 'default'"
                  size="large"
                  class="align-button w-full flex-1"
                  :class="{ 'align-button-active': output.align === 'left' }"
                  @click="handleTextFieldStyling('left', 'align')"
                >
                  <AlignLeftIcon class="h-5 w-5" />
                </UIButton>
                <UIButton
                  id="align-center"
                  :type="output.align === 'center' ? 'tertiary' : 'default'"
                  size="large"
                  class="align-button w-full flex-1"
                  :class="{ 'align-button-active': output.align === 'center' }"
                  @click="handleTextFieldStyling('center', 'align')"
                >
                  <AlignCenterIcon class="h-5 w-5" />
                </UIButton>
                <UIButton
                  id="align-right"
                  :type="output.align === 'right' ? 'tertiary' : 'default'"
                  size="large"
                  class="align-button w-full flex-1"
                  :class="{ 'align-button-active': output.align === 'right' }"
                  @click="handleTextFieldStyling('right', 'align')"
                >
                  <AlignRightIcon class="h-5 w-5" />
                </UIButton>
              </div>
            </div>
          </div>
        </UICollapseItem>

        <!-- Colors and Background Section -->
        <UICollapseItem id="colors-background" name="colors-background">
          <template #header>
            <div>{{ t("common.style.colorsAndBackground") }}</div>
          </template>
          <div class="collapse-content-container">
            <div class="collapse-content colors-background-content">
              <div class="flex flex-col gap-3">
                <!-- Button Background - Full Width -->
                <ColorInput
                  :label="t('common.buttonBackground')"
                  :color="output.bgColor"
                  @update:color="(value) => handleTextFieldStyling(value, 'background')"
                />

                <!-- Text and Subtext Colors in Grid -->
                <div class="grid grid-cols-2 gap-3">
                  <!-- Text Font Color -->
                  <ColorInput
                    :label="t('common.textFontColour')"
                    :color="output.color"
                    @update:color="(value) => handleTextFieldStyling(value, 'labelFontColor')"
                  />

                  <!-- Sub Text Font Color -->
                  <ColorInput
                    :label="t('common.subTextFontColour')"
                    :color="output.subTextColor"
                    @update:color="(value) => handleTextFieldStyling(value, 'subLabelFontColor')"
                  />
                </div>
              </div>
            </div>
          </div>
        </UICollapseItem>

        <!-- Border Section -->
        <UICollapseItem id="border" name="border">
          <template #header>
            <div>{{ t("common.border") }}</div>
          </template>
          <div class="collapse-content-container">
            <div class="collapse-content flex flex-col gap-3">
              <div class="grid grid-cols-2 gap-3">
                <PixelNumber
                  :label="t('common.style.borderWidth')"
                  :value="parseFloat(output?.border)"
                  @update:value="(value) => handleTextFieldStyling(value, 'border')"
                />
                <PixelNumber
                  :label="t('common.cornerRadius')"
                  :value="parseFloat(output?.radius)"
                  @update:value="(value) => handleTextFieldStyling(value, 'cornerRadius')"
                />
              </div>
              <ColorInput
                :label="t('common.style.borderColor')"
                :color="output?.borderColor"
                @update:color="(value) => handleTextFieldStyling(value, 'borderColor')"
              />
              <BorderStyle
                :label="t('common.style.borderStyle')"
                :border-type="output?.borderType"
                @update:border-type="(value) => handleTextFieldStyling(value, 'borderType')"
              />
            </div>
          </div>
        </UICollapseItem>

        <!-- Padding Section -->
        <UICollapseItem id="padding" name="padding">
          <template #header>
            <div>{{ t("common.padding") }}</div>
          </template>
          <div class="collapse-content-container">
            <div class="collapse-content">
              <PaddingInput
                :label="''"
                :padding="output.padding"
                :show-device-mode="false"
                @update-padding="handlePaddingUpdate"
              />
            </div>
          </div>
        </UICollapseItem>

        <!-- Shadow Section -->
        <UICollapseItem id="shadow" name="shadow">
          <template #header>
            <div>{{ t("common.shadow") }}</div>
          </template>
          <div class="collapse-content-container">
            <div class="collapse-content">
              <Shadow
                :label="''"
                :shadow="output.shadow"
                @update:horizontal="(value) => handleTextFieldStyling(value, 'shadowHorizontal')"
                @update:vertical="(value) => handleTextFieldStyling(value, 'shadowVertical')"
                @update:blur="(value) => handleTextFieldStyling(value, 'shadowBlur')"
                @update:spread="(value) => handleTextFieldStyling(value, 'shadowSpread')"
                @update:color="(value) => handleTextFieldStyling(value, 'shadowColor')"
              />
            </div>
          </div>
        </UICollapseItem>
      </UICollapse>
    </div>
  </section>
</template>

<style>
#button-sidebar-container .n-collapse-item__header {
  display: flex;
  align-items: start;
  padding: 16px;
  gap: 20px;
  border-bottom: 1px solid #eaecf0;
  background: #f9fafb;
}

#button-sidebar-container .n-collapse .n-collapse-item {
  margin-top: 0;
}

#button-sidebar-container .n-collapse-item__header-main {
  color: #667085;
}
</style>

<style scoped>
.custom-icons {
  color: #38a0db;
  margin: 0 10px;
}
.icons-collection {
  display: flex;
  align-items: center;
}
.icons-collection svg {
  cursor: pointer;
}
.alignment {
  display: flex;
  margin-bottom: 10px;
}
.alignment-div {
  margin-right: 10px;
}

.field-name-sidebar {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center !important;
  margin-top: 1rem;
}

.colors-background-content {
  align-items: normal !important;
}

/* Custom styling for alignment buttons */
.align-button {
  height: 44px !important;
  padding: 12px 14px !important;
  width: 100% !important;
  min-width: 0 !important;
  flex: 1 1 0 !important;
}

#button-sidebar-container .alignment-grid {
  width: 100% !important;
}

.align-button-active {
  background: #f5f8ff !important;
  border: 2px solid #155eef !important;
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05) !important;
}

.align-button-active svg {
  color: #155eef !important;
}

/* Ensure Sub Text container maintains width */
.subtext-container {
  min-width: 100%;
  width: 100%;
}

#button-sidebar-container .collapse-content .subtext-container {
  flex-shrink: 0;
}

/* Icon container styling */
.icons-container {
  min-width: 100%;
  width: 100%;
  flex-shrink: 0;
  overflow: visible;
}

#button-sidebar-container .collapse-content .icons-container {
  flex-shrink: 0;
}

/* Custom styling for icon placement buttons */
.placement-button {
  height: 44px !important;
  padding: 12px 14px !important;
  width: 100% !important;
  min-width: 0 !important;
  flex: 1 1 0 !important;
}

#button-sidebar-container .icon-placement-grid {
  width: 100% !important;
}

.placement-button-active {
  background: #f5f8ff !important;
  border: 2px solid #155eef !important;
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05) !important;
  color: #155eef !important;
}

/* Percent text styling to match PixelNumber */
.percent-text {
  color: #98a2b3;
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
}
</style>
